/**
 * R14's bounded metadata cache, as a **pure policy over a plain record**.
 *
 * R14 is written about video metadata for History, but the rule it encodes belongs to
 * every ID-only list: hydrating a row costs a large request, so the app must ask only for what it
 * does not already know, and the store must not grow without limit. This was hit one step earlier
 * from the other side — a subscription is `{author, authorId}` and nothing else, and the avatar needs
 * one ~50 KB `/api/v1/channels/{ucid}` call per channel — so the machinery lands here, generic, and
 * History instantiates it for videos rather than writing a second copy of the eviction rule.
 *
 * **Pure, and that is the whole design.** R1 forbids core touching storage, and R4 keeps `JSON.parse`
 * out of core, so this module never reads or writes anything: it takes the current state and returns
 * the next one. The platform owns "where the blob lives" and "how it is serialised", which is two
 * dozen lines of `localStorage` it can get right once (`platform/channelMetaStore.ts`).
 *
 * What core owns instead is the part that is easy to get subtly wrong and impossible to notice:
 *
 * - Eviction is **LRU on `lastSeenEpochSec`, not insertion order** (R14 says so explicitly). Insertion
 *   order evicts the entry a reader looks at every day in favour of one they saw once, which is the
 *   behaviour of a queue rather than a cache.
 * - A hit past the TTL is **still a hit** — returned for an instant render *and* reported as stale so
 *   the caller can refresh it in the background. A stale title beats an empty row; dropping it would
 *   make a 31-day-old cache exactly as expensive as no cache at all.
 * - The version is checked on read and a mismatch evicts **wholesale** rather than migrating (R3/R5):
 *   this is a cache, so throwing it away costs one round of requests and no correctness at all.
 */

/**
 * What every cached entry must carry. Deliberately structural rather than a base class: the entry
 * type belongs to the caller (a `CachedChannelMeta` here, R14's `CachedVideoMeta` in 3d) and all this
 * module needs to know is how to identify one and when it was last useful.
 */
export interface CacheEntry {
  /** The lookup key — a UCID here, a video id in 3d. */
  readonly id: string;
  readonly cachedAtEpochSec: number;
  /** Bumped on every hit. This, not insertion order, is what eviction sorts on. */
  readonly lastSeenEpochSec: number;
}

export interface MetaCachePolicy {
  /** R14's hard cap. Enforced on every put, so the blob has a size bound rather than a habit. */
  readonly maxEntries: number;
  /** R14's staleness TTL. A hit older than this renders *and* refreshes. */
  readonly ttlSec: number;
}

/** R14's defaults: 1000 entries ≈ 250 KB of video metadata, 30 days. */
export const DEFAULT_META_CACHE_POLICY: MetaCachePolicy = {
  maxEntries: 1000,
  ttlSec: 30 * 24 * 60 * 60,
};

/**
 * The persisted shape — R3/R5's versioned blob. `v` is the *schema* version and belongs to the entry
 * type, so the caller passes the one it expects and a mismatch is a wholesale eviction.
 */
export interface MetaCacheState<T extends CacheEntry> {
  readonly v: number;
  readonly entries: readonly T[];
}

export function emptyMetaCache<T extends CacheEntry>(version: number): MetaCacheState<T> {
  return { v: version, entries: [] };
}

/**
 * Read a persisted blob, or start over. Total: anything that is not this version's shape becomes an
 * empty cache, because a cache is the one kind of state where discarding is always safe.
 */
export function readMetaCache<T extends CacheEntry>(
  stored: MetaCacheState<T> | null,
  version: number,
): MetaCacheState<T> {
  if (stored === null || stored.v !== version || !Array.isArray(stored.entries)) {
    return emptyMetaCache<T>(version);
  }
  return stored;
}

export interface MetaCacheLookup<T extends CacheEntry> {
  /** Everything the cache knows, in the order the *ids* were asked for — not the cache's order. */
  readonly hits: readonly T[];
  /**
   * The subset of `hits` past the TTL. Render them, then refresh them. Separate from `misses` because
   * the two justify different urgency: a miss is a blank row, a stale hit is a row that is already
   * readable.
   */
  readonly stale: readonly T[];
  /** R14's whole point — fetch **only** these. */
  readonly misses: readonly string[];
}

/**
 * Look up several ids at once, because that is the shape of the question a list asks. A per-id
 * `get()` would work and would put the "which of these do I still need" loop in every caller, which
 * is exactly the loop that ends up fetching everything.
 *
 * Note what this does **not** do: it does not bump `lastSeenEpochSec`. A lookup is pure, and the
 * whole state is returned by {@link touchMetaCache} instead, so a caller that only rendered from the
 * cache still records the use without this function having two jobs.
 */
export function metaCacheLookup<T extends CacheEntry>(
  state: MetaCacheState<T>,
  ids: readonly string[],
  nowEpochSec: number,
  policy: MetaCachePolicy = DEFAULT_META_CACHE_POLICY,
): MetaCacheLookup<T> {
  const byId = new Map(state.entries.map((entry) => [entry.id, entry]));
  const hits: T[] = [];
  const stale: T[] = [];
  const misses: string[] = [];

  // Deduplicated, since a caller's id list can legitimately repeat (the same channel twice in one
  // feed page) and asking for one id twice is one request too many.
  for (const id of new Set(ids)) {
    const entry = byId.get(id);
    if (entry === undefined) {
      misses.push(id);
      continue;
    }
    hits.push(entry);
    if (nowEpochSec - entry.cachedAtEpochSec > policy.ttlSec) stale.push(entry);
  }

  return { hits, stale, misses };
}

/**
 * Record that these ids were used, without changing what they hold. This is the half of a hit that
 * keeps LRU meaningful: a cache whose `lastSeenEpochSec` is only written on *fetch* sorts by
 * insertion order wearing an LRU field's name.
 */
export function touchMetaCache<T extends CacheEntry>(
  state: MetaCacheState<T>,
  ids: readonly string[],
  nowEpochSec: number,
): MetaCacheState<T> {
  const wanted = new Set(ids);
  let changed = false;
  const entries = state.entries.map((entry) => {
    if (!wanted.has(entry.id) || entry.lastSeenEpochSec === nowEpochSec) return entry;
    changed = true;
    return { ...entry, lastSeenEpochSec: nowEpochSec };
  });
  // Returning the same object when nothing moved matters to React: this state ends up in a store,
  // and a new array every render is a re-render every render.
  return changed ? { v: state.v, entries } : state;
}

/**
 * Insert or replace, then enforce the cap.
 *
 * Newer data wins on a collision rather than being merged, because the entry *is* the payload: a
 * partial merge would leave a row half from a channel record fetched today and half from one fetched
 * a month ago, which is a state no test can construct on purpose and every reader would see.
 */
export function metaCachePut<T extends CacheEntry>(
  state: MetaCacheState<T>,
  metas: readonly T[],
  policy: MetaCachePolicy = DEFAULT_META_CACHE_POLICY,
): MetaCacheState<T> {
  if (metas.length === 0) return state;

  const byId = new Map(state.entries.map((entry) => [entry.id, entry]));
  for (const meta of metas) byId.set(meta.id, meta);

  return { v: state.v, entries: evict([...byId.values()], policy) };
}

/**
 * R14's hard cap, applied on every put.
 *
 * Sorted **descending by `lastSeenEpochSec`** and truncated, so what survives is what was used most
 * recently. The tie-break is `cachedAtEpochSec`, which only matters on the first visit of a session —
 * every row of one page is touched in the same second — and there the fresher record is the better
 * one to keep.
 */
function evict<T extends CacheEntry>(entries: readonly T[], policy: MetaCachePolicy): readonly T[] {
  const cap = Math.max(0, Math.floor(policy.maxEntries));
  if (entries.length <= cap) return entries;

  return [...entries]
    .sort((a, b) => b.lastSeenEpochSec - a.lastSeenEpochSec || b.cachedAtEpochSec - a.cachedAtEpochSec)
    .slice(0, cap);
}
